import * as React from "react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";

/**
 * Shadcn-style searchable select (Combobox pattern).
 *
 * Props:
 * - value: string
 * - onValueChange: (value: string) => void
 * - options: Array<{ value: string, label: string }>
 * - placeholder?: string
 * - labelPrefix?: string   (e.g. "Status:" -> shown inside trigger as a single block)
 * - className?: string     (applies to trigger)
 * - contentClassName?: string
 */
export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder,
  labelPrefix,
  className,
  contentClassName,
  disabled,
  displayOverride,
}) {
  const [open, setOpen] = React.useState(false);

  const selected = options.find((o) => String(o.value) === String(value));
  const displayValue = displayOverride || selected?.label || "";

  return (
    <Popover open={!disabled && open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-10 min-w-[160px] items-center justify-between gap-2 rounded-lg border border-[#2F7F571A] bg-[#F6F8F7] px-3 py-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-[#2F7F57] focus:ring-offset-2",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
          aria-expanded={open}
        >
          <span className="min-w-0 flex-1 truncate">
            {labelPrefix ? (
              <span className="truncate">
                <span className="mr-2">{labelPrefix}</span>
                <span className="font-medium">
                  {displayValue || placeholder || "Select"}
                </span>
              </span>
            ) : (
              <span className={cn(!displayValue && "opacity-70")}>
                {displayValue || placeholder || "Select"}
              </span>
            )}
          </span>
          <ChevronDown className="h-4 w-4 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-[--radix-popover-trigger-width] p-0",
          contentClassName,
        )}
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onValueChange(opt.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      opt.value === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
